/**
 * Inline SVG icons drawn from the Figma "Mobile — Flows v2" components section (46:124).
 * They are components, not files: a phone must never spend a request on a 300-byte glyph,
 * and `currentColor` lets the tab bar recolor them without a second asset.
 */
import type { SVGProps } from 'react';

export type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function Svg({ size = 24, children, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

/** Tab bar — Home (46:141). */
export function IconHome(props: IconProps) {
  return (
    <Svg {...props}>
      <path
        d="M3.5 10.2 12 4l8.5 6.2V20a1 1 0 0 1-1 1h-15a1 1 0 0 1-1-1v-9.8Z"
        fill="currentColor"
      />
    </Svg>
  );
}

/** Tab bar — Search (46:147). */
export function IconSearch(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="10.5" cy="10.5" r="6.5" stroke="currentColor" strokeWidth="2" />
      <path d="m15.5 15.5 5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </Svg>
  );
}

/** Tab bar — Profile (46:153). */
export function IconProfile(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="8" r="4" fill="currentColor" />
      <path d="M4 20a8 8 0 0 1 16 0Z" fill="currentColor" />
    </Svg>
  );
}

/**
 * Tab bar — Queue (46:159).
 *
 * UX-417: three stacked bars are a hamburger, and on a phone that reads as "menu" — which is
 * what the one tab that carries a badge, and therefore the one people look for first, was
 * wearing. It is a list *of songs* now: two rows plus a note waiting at the end of them.
 */
export function IconQueue(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="2" y="4.5" width="15" height="2.6" rx="1.3" fill="currentColor" />
      <rect x="2" y="10.7" width="15" height="2.6" rx="1.3" fill="currentColor" />
      <rect x="2" y="16.9" width="9" height="2.6" rx="1.3" fill="currentColor" />
      <circle cx="16.4" cy="19" r="2.6" fill="currentColor" />
      <rect x="18" y="9.6" width="2.2" height="9.4" rx="1.1" fill="currentColor" />
      <path d="M18 9.6 22 8.2v3l-4 1.4z" fill="currentColor" />
    </Svg>
  );
}

/** The "TV K7PX4M" chip (48:86): a screen with a stand. */
export function IconTv({ size = 16, ...rest }: IconProps) {
  return (
    <Svg size={size} viewBox="0 0 16 16" {...rest}>
      <rect x="1.33" y="2.67" width="13.33" height="8.67" rx="1.33" fill="currentColor" />
      <rect x="5.33" y="12.67" width="5.33" height="1.33" rx="0.67" fill="currentColor" />
    </Svg>
  );
}

/** Camera glyph of the optional guest avatar (M-B2 49:243). */
export function IconCamera({ size = 36, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 36 36"
      fill="none"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      <rect x="3" y="9" width="30" height="21" rx="4.5" fill="currentColor" />
      <circle cx="18" cy="19.5" r="6" fill="var(--akm-surface)" />
    </svg>
  );
}

/** Back chevron of the M-B4 header (49:415). */
export function IconBack(props: IconProps) {
  return (
    <Svg {...props}>
      <path
        d="M20 12H4m0 0 6-6m-6 6 6 6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

/** Toast check (46:365). */
export function IconCheck(props: IconProps) {
  return (
    <Svg {...props}>
      <path
        d="m4 12.5 5 5L20 6.5"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

/** Disclosure chevron of "¿Tienes un código?" (48:250). */
export function IconChevronDown(props: IconProps) {
  return (
    <Svg {...props}>
      <path
        d="m6 9.5 6 6 6-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

export function IconAlert(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
      <path d="M12 7.5v5.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <circle cx="12" cy="16.5" r="1.25" fill="currentColor" />
    </Svg>
  );
}
